import type { components } from '@/lib/api/generated/schema'
import {
  api,
  normalizePrivateProfile,
  normalizeProfileSubset,
  normalizePublicProfile,
} from '@/lib/api'

type Schemas = components['schemas']

export type PrivateProfile = Schemas['PrivateProfile']
export type ProfileSubset = Schemas['ProfileSubset']
export type PublicProfile = Schemas['PublicProfile']
export type UpdateProfileRequest = Schemas['UpdateProfileRequest']
export type FriendsResponse = Schemas['FriendsResponse']
export type Friend = Schemas['Friend']
export type FriendSearchItem = Schemas['FriendSearchItem']
export type FriendSearchResponse = Schemas['FriendSearchResponse']
export type RelationshipResponse = Schemas['RelationshipResponse']
export type FriendRequestResponse = Schemas['FriendRequestResponse']
export type FriendDecisionResponse = Schemas['FriendDecisionResponse']
export type ReportReason = Schemas['ReportReason']
export type CreateReportRequest = Schemas['CreateReportRequest']
export type CreateReportResponse = Schemas['CreateReportResponse']
export type ReverseGeocodeResponse = Schemas['ReverseGeocodeResponse']
export type OpenConversationResponse = Schemas['OpenConversationResponse']

interface RequestOptions {
  signal?: AbortSignal
}

/**
 * Thin feature boundary over the frozen `/api/v1` profile, friends, block and
 * report endpoints. Nothing is cached here: the backend owns server state
 * (D-042) and each screen reconciles its own mutations.
 */
export const profileSocialApi = {
  getMe(options: RequestOptions = {}): Promise<PrivateProfile> {
    return api
      .get<PrivateProfile>('/me', { signal: options.signal })
      .then(normalizePrivateProfile)
  },

  updateMe(body: UpdateProfileRequest): Promise<ProfileSubset> {
    return api
      .patch<ProfileSubset>('/me', body)
      .then(normalizeProfileSubset)
  },

  uploadAvatar(file: File): Promise<ProfileSubset> {
    const form = new FormData()
    form.append('file', file)
    return api
      .post<ProfileSubset>('/me/avatar', form)
      .then(normalizeProfileSubset)
  },

  deleteMe(): Promise<Schemas['OkResponse']> {
    return api.delete<Schemas['OkResponse']>('/me')
  },

  getPublicProfile(
    userId: string,
    options: RequestOptions = {},
  ): Promise<PublicProfile> {
    return api
      .get<PublicProfile>(`/users/${encodeURIComponent(userId)}`, {
        signal: options.signal,
      })
      .then(normalizePublicProfile)
  },

  blockUser(userId: string): Promise<Schemas['BlockResponse']> {
    return api.post<Schemas['BlockResponse']>(
      `/users/${encodeURIComponent(userId)}/block`,
    )
  },

  unblockUser(userId: string): Promise<Schemas['BlockResponse']> {
    return api.delete<Schemas['BlockResponse']>(
      `/users/${encodeURIComponent(userId)}/block`,
    )
  },

  listFriends(options: RequestOptions = {}): Promise<FriendsResponse> {
    return api.get<FriendsResponse>('/friends', { signal: options.signal })
  },

  searchFriends(
    query: string,
    options: RequestOptions = {},
  ): Promise<FriendSearchResponse> {
    return api.get<FriendSearchResponse>('/friends/search', {
      query: { q: query },
      signal: options.signal,
    })
  },

  getRelationship(
    userId: string,
    options: RequestOptions = {},
  ): Promise<RelationshipResponse> {
    return api.get<RelationshipResponse>(
      `/friends/relationship/${encodeURIComponent(userId)}`,
      { signal: options.signal },
    )
  },

  sendFriendRequest(userId: string): Promise<FriendRequestResponse> {
    return api.post<FriendRequestResponse>('/friends/requests', { userId })
  },

  acceptFriendRequest(
    requestId: string,
  ): Promise<FriendDecisionResponse> {
    return api.post<FriendDecisionResponse>(
      `/friends/requests/${encodeURIComponent(requestId)}/accept`,
    )
  },

  rejectFriendRequest(
    requestId: string,
  ): Promise<FriendDecisionResponse> {
    return api.post<FriendDecisionResponse>(
      `/friends/requests/${encodeURIComponent(requestId)}/reject`,
    )
  },

  removeFriend(userId: string): Promise<Schemas['OkResponse']> {
    return api.delete<Schemas['OkResponse']>(
      `/friends/${encodeURIComponent(userId)}`,
    )
  },

  openConversation(userId: string): Promise<OpenConversationResponse> {
    return api.post<OpenConversationResponse>(
      `/private-conversations/${encodeURIComponent(userId)}`,
    )
  },

  report(body: CreateReportRequest): Promise<CreateReportResponse> {
    return api.post<CreateReportResponse>('/reports', body)
  },

  reverseGeocode(
    latitude: number,
    longitude: number,
    options: RequestOptions = {},
  ): Promise<ReverseGeocodeResponse> {
    return api.post<ReverseGeocodeResponse>(
      '/location/reverse-geocode',
      { latitude, longitude },
      { signal: options.signal, skipAuth: true, skipRefresh: true },
    )
  },
}
